import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import SendMessageModal from '../Messaging/SendMessageModal';

const PractitionerListing = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [practitioners, setPractitioners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    learningInterest: '',
    language: '',
    timezone: '',
    ageBracket: '',
    gender: '',
    experienceLevel: ''
  });

  const learningInterests = [
    'Interview Preparation',
    'Public Speaking',
    'Dating/Flirting',
    'Business Communication',
    'Language Practice',
    'Social Skills',
    'Presentation Skills',
    'Networking',
    'Confidence Building',
    'Accent Training'
  ];

  const languageOptions = [
    'English', 'Spanish', 'French', 'German', 'Italian', 'Portuguese', 'Chinese',
    'Japanese', 'Korean', 'Arabic', 'Russian', 'Hindi', 'Dutch', 'Swedish',
    'Norwegian', 'Polish', 'Turkish', 'Hebrew', 'Thai', 'Vietnamese'
  ];

  const timezoneOptions = [
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'America/Anchorage', 'Pacific/Honolulu', 'America/Toronto', 'America/Vancouver',
    'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Rome', 'Europe/Madrid',
    'Europe/Amsterdam', 'Europe/Stockholm', 'Europe/Zurich', 'Europe/Dublin',
    'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Seoul', 'Asia/Hong_Kong', 'Asia/Singapore',
    'Asia/Kolkata', 'Asia/Dubai', 'Asia/Bangkok', 'Asia/Manila', 'Asia/Jakarta',
    'Australia/Sydney', 'Australia/Melbourne', 'Australia/Perth', 'Pacific/Auckland',
    'America/Sao_Paulo', 'America/Argentina/Buenos_Aires', 'America/Mexico_City',
    'Africa/Cairo', 'Africa/Johannesburg', 'Africa/Lagos'
  ];

  const ageBracketOptions = [
    '18-25', '26-35', '36-45', '46-55', '56-65', '65+'
  ];

  const genderOptions = [
    'Male', 'Female'
  ];

  const experienceLevelOptions = [
    'Beginner', 'Intermediate', 'Advanced'
  ];

  useEffect(() => {
    fetchPractitioners();
    if (currentUser) {
      fetchCurrentUserData();
    }
  }, [currentUser]);

  const fetchPractitioners = async () => {
    try {
      // First, try a more lenient query to see what practitioners exist
      const q = query(
        collection(db, 'users'),
        where('userType', 'in', ['practitioner', 'both'])
      );
      
      const querySnapshot = await getDocs(q);
      const practitionersData = [];
      
      querySnapshot.forEach((doc) => {
        if (currentUser && doc.id === currentUser.uid) {
          return;
        }
        
        const userData = doc.data();
        
        // Filter in code instead of query to be more flexible
        // Include if they have practitioner profile data OR if practitionerProfileComplete is true
        const hasPractitionerData = userData.learningInterests || 
                                   userData.practitionerBio || 
                                   userData.practitionerProfileComplete;
        
        if (hasPractitionerData) {
          const practitionerData = {
            id: doc.id,
            ...userData
          };
          practitionersData.push(practitionerData);
        }
      });
      
      console.log('Found practitioners:', practitionersData.length);
      console.log('Sample practitioner data:', practitionersData[0]);
      
      setPractitioners(practitionersData);
    } catch (error) {
      console.error('Error fetching practitioners:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentUserData = async () => {
    if (!currentUser) return;
    
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        setCurrentUserData({ id: userDoc.id, ...userDoc.data() });
      }
    } catch (error) {
      console.error('Error fetching current user data:', error);
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      search: '',
      learningInterest: '',
      language: '',
      timezone: '',
      ageBracket: '',
      gender: '',
      experienceLevel: ''
    });
  };

  const handleSendMessage = (e, practitioner) => {
    e.stopPropagation();
    if (!currentUser) {
      const currentPath = window.location.pathname + window.location.search;
      navigate(`/login?redirect=${encodeURIComponent(currentPath)}`);
      return;
    }
    setSelectedPractitioner(practitioner);
    setShowMessageModal(true);
  };

  const filteredPractitioners = practitioners.filter(practitioner => {
    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      const fullName = `${practitioner.firstName || ''} ${practitioner.lastName || ''}`.toLowerCase();
      const bio = (practitioner.practitionerBio || '').toLowerCase();
      const goals = (practitioner.goals || '').toLowerCase();
      
      const matchesSearch = fullName.includes(searchTerm) || 
                           bio.includes(searchTerm) || 
                           goals.includes(searchTerm);
      
      if (!matchesSearch) return false;
    }
    
    if (filters.learningInterest && !practitioner.learningInterests?.includes(filters.learningInterest)) return false;
    if (filters.language && !practitioner.languages?.includes(filters.language)) return false;
    if (filters.timezone && practitioner.timezone !== filters.timezone) return false;
    if (filters.ageBracket && practitioner.ageBracket !== filters.ageBracket) return false;
    if (filters.gender && practitioner.gender !== filters.gender) return false;
    if (filters.experienceLevel && practitioner.experienceLevel !== filters.experienceLevel) return false;
    
    return true;
  });

  if (loading) {
    return (
      <div className="provider-listing">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Finding practitioners to connect with...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="provider-listing">
      <div className="provider-listing-header">
        <h1>Connect with Practitioners</h1>
        <p>Find and connect with practitioners who are looking to improve their communication skills</p>
      </div>

      {/* Search and Filters */}
      <div className="filters-container">
        <div className="search-section">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search by name, bio, or goals..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="form-input search-input"
            />
          </div>
        </div>

        <div className="filters-grid">
          <div className="filter-group">
            <label>Learning Interests</label>
            <select 
              value={filters.learningInterest} 
              onChange={(e) => handleFilterChange('learningInterest', e.target.value)}
              className="form-select"
            >
              <option value="">All Interests</option>
              {learningInterests.map(interest => (
                <option key={interest} value={interest}>{interest}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Language</label>
            <select 
              value={filters.language} 
              onChange={(e) => handleFilterChange('language', e.target.value)}
              className="form-select"
            >
              <option value="">All Languages</option>
              {languageOptions.map(language => (
                <option key={language} value={language}>{language}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Timezone</label>
            <select 
              value={filters.timezone} 
              onChange={(e) => handleFilterChange('timezone', e.target.value)}
              className="form-select"
            >
              <option value="">All Timezones</option>
              {timezoneOptions.map(timezone => (
                <option key={timezone} value={timezone}>{timezone.replace('_', ' ')}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Experience Level</label>
            <select 
              value={filters.experienceLevel} 
              onChange={(e) => handleFilterChange('experienceLevel', e.target.value)}
              className="form-select"
            >
              <option value="">All Levels</option>
              {experienceLevelOptions.map(level => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Age Bracket</label>
            <select 
              value={filters.ageBracket} 
              onChange={(e) => handleFilterChange('ageBracket', e.target.value)}
              className="form-select"
            >
              <option value="">All Ages</option>
              {ageBracketOptions.map(age => (
                <option key={age} value={age}>{age}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Gender</label>
            <select 
              value={filters.gender} 
              onChange={(e) => handleFilterChange('gender', e.target.value)}
              className="form-select"
            >
              <option value="">All Genders</option>
              {genderOptions.map(gender => (
                <option key={gender} value={gender}>{gender}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="filter-actions">
          <button onClick={clearAllFilters} className="btn-secondary">
            Clear All Filters
          </button>
          <span className="results-count">
            {filteredPractitioners.length} practitioner{filteredPractitioners.length !== 1 ? 's' : ''} found
          </span>
        </div>
      </div>

      {/* Practitioner Results */}
      <div className="providers-section">
        {filteredPractitioners.length === 0 ? (
          <div className="no-results">
            <div className="no-results-icon">🔍</div>
            <h3>No practitioners found</h3>
            <p>Try adjusting your filters to find more practitioners</p>
            <button onClick={clearAllFilters} className="btn-primary">
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="providers-grid">
            {filteredPractitioners.map(practitioner => {
              return (
                <div key={practitioner.id} className="provider-card">
                  <div className="provider-header-compact">
                    <div className="provider-main-info">
                      <div className="provider-avatar-small">
                        {practitioner.profilePicture ? (
                          <img src={practitioner.profilePicture} alt={`${practitioner.firstName} ${practitioner.lastName}`} />
                        ) : (
                          <div className="avatar-placeholder-small">
                            {practitioner.firstName?.[0]}{practitioner.lastName?.[0]}
                          </div>
                        )}
                      </div>
                      <div className="provider-basic-info">
                        <h3 className="provider-name-compact">{practitioner.firstName} {practitioner.lastName}</h3>
                        <div className="provider-location-compact">
                          🌍 {practitioner.timezone?.replace('_', ' ') || 'Not specified'}
                        </div>
                        <div className="provider-location-compact">
                          📈 {practitioner.experienceLevel || 'Not specified'}
                        </div>
                      </div>
                    </div>
                    <div className="provider-rate-availability">
                      <div className="provider-rate-top">
                        <span className="rate-amount-small">{practitioner.ageBracket || 'Not specified'}</span>
                      </div>
                      <div className="provider-rate-top">
                        <span className="rate-amount-small">{practitioner.gender || 'Not specified'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="provider-bio">
                    <p>{(practitioner.practitionerBio || 'No bio available').substring(0, 100)}{practitioner.practitionerBio?.length > 100 ? '...' : ''}</p>
                  </div>

                  <div className="provider-details">
                    <div className="provider-skills">
                      <h4>Learning Interests</h4>
                      <div className="skills-list">
                        {practitioner.learningInterests?.slice(0, 3).map(interest => (
                          <span key={interest} className="skill-tag">{interest}</span>
                        ))}
                        {practitioner.learningInterests?.length > 3 && (
                          <span className="skill-tag more">+{practitioner.learningInterests.length - 3}</span>
                        )}
                      </div>
                    </div>

                    {practitioner.languages && practitioner.languages.length > 0 && (
                      <div className="provider-languages-compact">
                        <h4>Languages</h4>
                        <p>{practitioner.languages.slice(0, 3).join(', ')}{practitioner.languages.length > 3 ? ` +${practitioner.languages.length - 3} more` : ''}</p>
                      </div>
                    )}

                    {practitioner.goals && (
                      <div className="provider-expertise-compact">
                        <h4>Goals</h4>
                        <p>{practitioner.goals.substring(0, 60)}{practitioner.goals.length > 60 ? '...' : ''}</p>
                      </div>
                    )}
                  </div>

                  <div className="provider-actions">
                    <button 
                      className="message-btn-compact" 
                      onClick={(e) => handleSendMessage(e, practitioner)}
                    >
                      {currentUser ? 'Send Message' : 'Sign In to Message'}
                    </button>
                    <button 
                      className="view-profile-btn-compact"
                      onClick={() => navigate(`/practitioner/${practitioner.username || practitioner.id}`)}
                    >
                      View Profile
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <SendMessageModal
        isOpen={showMessageModal}
        onClose={() => {
          setShowMessageModal(false);
          setSelectedPractitioner(null);
        }}
        recipient={selectedPractitioner}
        currentUserData={currentUserData}
      />
    </div>
  );
};

export default PractitionerListing;